import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments';
import {
  ImportFileReviewResultDto,
  ImportFileStatusResultDto,
  ImportFileSummaryResponse,
  ImportFileValidationResponse,
  TransactionFileUploadResultModel,
} from '@models/transaction-file.models';
import { map, Observable, take } from 'rxjs';
import { downloadFileFromResponse } from 'utils/file-downloader';

@Injectable({
  providedIn: 'root',
})
export class TransactionFilesService {
  readonly #http = inject(HttpClient);
  static readonly BASE_URL = `${environment.apiConfig.baseUrl}/cbam/import-file`;

  public uploadFile(file: File, legalEntityId: string, templateId: string) {
    const url = `${TransactionFilesService.BASE_URL}/validate`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('legalEntityId', legalEntityId);
    formData.append('templateId', templateId);

    return this.#http.post<TransactionFileUploadResultModel>(url, formData).pipe(
      map(r => new TransactionFileUploadResultModel(r.importFileId, r.errorMessage, r.errorDetailMessages)),
      take(1)
    );
  }

  public validateFileSubmission(importFileId: string): Observable<ImportFileValidationResponse> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/validation-result`;
    return this.#http.get<ImportFileValidationResponse>(url).pipe(take(1));
  }

  public submitFile(importFileId: string): Observable<void> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/submit`;
    return this.#http.post<void>(url, {}).pipe(take(1));
  }

  public requestApproval(importFileId: string, comment: string): Observable<ImportFileReviewResultDto> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/request-approval`;
    return this.#http.post<ImportFileReviewResultDto>(url, { comment }).pipe(take(1));
  }

  public approveFile(importFileId: string, comment: string): Observable<ImportFileReviewResultDto> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/approve`;
    return this.#http.post<ImportFileReviewResultDto>(url, { comment }).pipe(take(1));
  }

  public rejectFile(importFileId: string, comment: string): Observable<ImportFileReviewResultDto> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/reject`;
    return this.#http.post<ImportFileReviewResultDto>(url, { comment }).pipe(take(1));
  }

  public reValidateFile(importFileId: string): Observable<ImportFileReviewResultDto> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/re-validate`;
    return this.#http.post<ImportFileReviewResultDto>(url, {}).pipe(take(1));
  }

  public getImportFileStatus(importFileId: string): Observable<ImportFileStatusResultDto> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/status`;
    return this.#http.get<ImportFileStatusResultDto>(url).pipe(take(1));
  }

  public getFileSummary(importFileId: string): Observable<ImportFileSummaryResponse> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}`;
    return this.#http.get<ImportFileSummaryResponse>(url, {}).pipe(take(1));
  }

  public downloadImportFile(importFileId: string): Observable<Blob> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/download`;

    return this.#http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map(response => {
        downloadFileFromResponse(response);
        return response.body!;
      })
    );
  }

  public downloadValidationReport(importFileId: string): Observable<Blob> {
    const url = `${TransactionFilesService.BASE_URL}/${importFileId}/download-validation-report`;

    return this.#http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      map(response => {
        downloadFileFromResponse(response);
        return response.body!;
      })
    );
  }
}
